/**
 * @date: 2022-08-08
 * @author: Kevin Hsiau <khsiau@max.md>
 * @copy: (C) Copyright 2022 MaxMD Corporation, All Rights Reserved.
 */

import React, {useEffect, useState} from "react";
import {Alert, Backdrop, Button, CircularProgress, Container, Pagination, Paper, styled} from "@mui/material";
import ExternalSourceBreadcrumbs from "../ExternalSourceManagement/ExternalSourceBreadcrumbs";
import {Link, useLocation} from "react-router-dom";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import {ApiUrl, PortalUrl} from "../utils/portal-urls";
import {getQueryParams, handleHttpResponse} from "../utils/http-helpers";
import RefreshIcon from "@mui/icons-material/Refresh";
import clsx from "clsx";
import ShowSnackbar from "../utils/ShowSnackbar";
import {batchOnRefresh, getAllBatches} from "../features/allBatchesSlice";
import {useDispatch, useSelector} from "react-redux";
import {batchCurrentStatus} from "../ExternalSourceManagement/ExternalSourceManagementHome";
import ExternalSourceBatchProgressBar from "../ExternalSourceManagement/ExternalSourceBatchProgressBar";
import SuffixLogsTable from "./SuffixLogsTable";

const StyledContainer = styled(Container)(({theme}) => ({
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}))
const StyledPaper = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2),
    boxShadow: '0 7px 20px #cccccc',
    minHeight: 450,
    borderRadius: '15px',
}))
const StyledBatchDiv = styled('div') ({
    marginBottom: '20px',
    '&:last-child': {
        marginBottom: 0,
    }
})
const StyledDiv = styled('div')({
    display: "flex",
    float: "right",
})
const StyledBatchPaper = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2),
}))

export default function SuffixHome(props) {
    const location = useLocation();
    const path = location.pathname;

    const getParameters = getQueryParams();
    const dynamicHeightPaper = clsx(StyledPaper, {minHeight: 120});

    const [suffixPageResult, setSuffixPageResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState({open: false, msg: '', msgLv: ''});
    const [page, setPage] = useState(getParameters.page ? parseInt(getParameters.page) : 0);
    const [query, setQuery] = useState("");
    const [queryCount, setQueryCount] = useState(0);
    const [currentViewingQuery, setCurrentViewingQuery] = useState(null);
    const [refresh, setRefresh] = useState(new Date().getTime());

    const dispatch = useDispatch();
    const {suffixBatches, batchRefresh} = useSelector(state => state.allBatches);

    useEffect(() => {
        console.info("Page changed or refreshed, useEffect fetch: ", query, page);
        if (query !== currentViewingQuery && queryCount > 0 && page > 0) {
            setPage(0);
        } else {
            fetchSuffixLogsPage("suffix", query, page);

        }

        dispatch(getAllBatches());

    }, [query, page, refresh, batchRefresh])


    const onRefresh = () => {
        setRefresh(new Date().getTime());
    }

    const fetchSuffixLogsPage = async (source, query, page) => {
        setLoading(true);
        const url = ApiUrl.ExternalSource.getExternalSourceLogs.getPath(source, query, page)
        console.info("Getting Suffix logs from: " + url);
        try {
            const res = await fetch(url)
            const data = await handleHttpResponse(res);
            setSuffixPageResult(data);
            setCurrentViewingQuery(query);
        } catch (err) {
            console.error("Fail to load Suffix logs, " + err);
            setSnackbarMsg({open: true, msg: "Fail to load Suffix logs", msgLv: 'error'});
        } finally {
            setLoading(false);
        }
    }

    return (
        <StyledContainer maxWidth='xl'>
            <ExternalSourceBreadcrumbs path={path}/>
            <h3 className='title'>
                Suffix
                <StyledDiv>
                    <Button variant="outlined" component={Link}
                            to={PortalUrl.ExternalSource.Suffix.pattern_new_task} endIcon={<AddIcon/>}>
                        New Import Task
                    </Button>
                    <Button variant="outlined" onClick={() => onRefresh()} endIcon={<RefreshIcon/>}>Refresh</Button>
                </StyledDiv>
            </h3>

            <Grid container spacing={3}>
                <Grid item xs={12} md={12} lg={12}>
                    <h4>Suffix Logs</h4>
                    <StyledPaper>
                        {suffixPageResult?.content?.length > 0 &&
                            <div>
                                <SuffixLogsTable suffixLogs={suffixPageResult.content} />
                                <br/>
                                {suffixPageResult.totalPages >= 1 &&
                                    <Pagination page={page + 1} count={suffixPageResult.totalPages}
                                                variant='outlined' color='primary' onChange={(e, newPage) => setPage(newPage - 1)} />}
                            </div>
                        }
                    </StyledPaper>

                </Grid>
                <Grid item xs={12} md={12} lg={12} sx={{mt: '25px'}}>
                    <h4>Current Suffix Progress Information</h4>

                    {suffixBatches.length > 0 ?
                        suffixBatches.map( b => (
                            <StyledBatchDiv key={b.id}>
                                <StyledBatchPaper className={dynamicHeightPaper}>
                                    {b.status &&
                                        <Alert severity={batchCurrentStatus[b.status].getStatus}
                                               icon={false}>{batchCurrentStatus[b.status].getMsg("Suffix Import Task")}</Alert>}
                                    <br/>
                                    {b.id && b.status !== 'Completed' && b.status !== 'Cancelled' && b.status !== 'Failed' &&
                                        <ExternalSourceBatchProgressBar batch={b}/>}
                                </StyledBatchPaper>
                            </StyledBatchDiv>
                        )) :
                        <StyledPaper sx={{minHeight: '200px'}}>
                            {<Alert severity='info' icon={false}>No Active Process</Alert> }
                        </StyledPaper>
                    }

                </Grid>
            </Grid>
            <ShowSnackbar open={snackbarMsg.open} close={(c) => setSnackbarMsg(c)} msg={snackbarMsg}/>
            <Backdrop sx={{zIndex: theme => theme.zIndex.drawer + 1, color: "#fff"}} open={loading}>
                <CircularProgress color={"inherit"}/>
            </Backdrop>
        </StyledContainer>
    )
}